import { Box, Button, Flex, Form, Grid, Label, Popup, Radio, Text } from '@approvalmax/ui/src/components';
import { memo, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMount, useUnmount } from 'react-use';
import { PaymentDetailsLegalEntityType } from 'shared/data/webApp/v2';

import { BenificaryNameVariant, FormValues } from '../../EditCounterpartyPaymentDetailsPopupContent.types';
import { messages } from './IncorrectNamePopupContent.messages';
import { IncorrectNamePopupContentProps } from './IncorrectNamePopupContent.types';

export const IncorrectNamePopupContent = memo<IncorrectNamePopupContentProps>((props) => {
    const { suggestedName, isLoading, onBack } = props;

    const form = useFormContext<FormValues>();

    const [legalEntityType, companyName, firstName, lastName, selectedBenificaryName] = form.watch([
        'legalEntity.legalEntityType',
        'legalEntity.companyName',
        'legalEntity.firstName',
        'legalEntity.lastName',
        'selectedBenificaryName',
    ]);

    useMount(() => {
        form.setValue('selectedBenificaryName', BenificaryNameVariant.Provided);
    });

    useUnmount(() => {
        form.setValue('selectedBenificaryName', null);
    });

    const options = useMemo(() => {
        return [
            {
                value: BenificaryNameVariant.Provided,
                label: messages.providedName,
                name:
                    legalEntityType === PaymentDetailsLegalEntityType.Company
                        ? companyName
                        : `${firstName} ${lastName}`,
            },
            {
                value: BenificaryNameVariant.Suggested,
                label: messages.suggestedName,
                name: suggestedName,
            },
        ];
    }, [legalEntityType, companyName, firstName, lastName, suggestedName]);

    return (
        <Form.Part>
            <Popup.Header title={messages.title} />

            <Popup.Body>
                <Grid rowGap={24}>
                    <Text font='body' fontSize='medium'>
                        {messages.description}
                    </Text>

                    <Radio.Group name='selectedBenificaryName' block>
                        <Grid width='100%' rowGap={16}>
                            {options.map(({ value, label, name }) => (
                                <Box
                                    key={value}
                                    bordered
                                    borderColor={selectedBenificaryName === value ? 'blue100' : 'midnight40'}
                                    radius='small'
                                    spacing='20 24'
                                >
                                    <Grid rowGap={16}>
                                        <Radio value={value}>{label}</Radio>

                                        <Flex spacing='8'>
                                            <Label size='medium'>{messages.name}</Label>

                                            <Text font='label' fontSize='medium'>
                                                {name}
                                            </Text>
                                        </Flex>
                                    </Grid>
                                </Box>
                            ))}
                        </Grid>
                    </Radio.Group>

                    <Grid gridTemplateColumns='max-content max-content' gap={16} justifyContent='flex-end'>
                        <Button disabled={isLoading} color='midnight40' size='large' onClick={onBack}>
                            {messages.back}
                        </Button>

                        <Button color='blue80' size='large' progress={isLoading} type='submit'>
                            {messages.confirm}
                        </Button>
                    </Grid>
                </Grid>
            </Popup.Body>
        </Form.Part>
    );
});

IncorrectNamePopupContent.displayName = 'IncorrectNamePopupContent';
